import React, { useState, useEffect } from 'react';
import { Star, ArrowLeft, MessageSquare, Search, SlidersHorizontal, ChevronLeft, ChevronRight, CheckCircle, Plus } from 'lucide-react';
import { api } from '../config/api';
import { Link } from 'react-router-dom';
import LeaveReview from '../components/home/LeaveReview';

const avatarColors = ['#D4AF37', '#4CAF50', '#E74C3C', '#3498DB', '#9B59B6', '#1ABC9C', '#E67E22'];

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Search & Filter state
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, highest, lowest
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsPerPage] = useState(6);
  
  // Modal/Show leave review form
  const [showLeaveReview, setShowLeaveReview] = useState(false);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reviews.php?limit=100'); // fetch more for local filtering/sorting
      if (res.data.success) {
        setReviews(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  // Filter & Sort logic
  const filteredReviews = reviews
    .filter(review => {
      const name = (review.display_name || review.name || '').toLowerCase();
      const comment = (review.comment || '').toLowerCase();
      const matchesSearch = name.includes(search.toLowerCase()) || comment.includes(search.toLowerCase());
      
      const matchesRating = ratingFilter === 'all' || Number(review.rating) === Number(ratingFilter);
      
      return matchesSearch && matchesRating;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.created_at || Date.now()) - new Date(a.created_at || Date.now());
      if (sortBy === 'oldest') return new Date(a.created_at || Date.now()) - new Date(b.created_at || Date.now());
      if (sortBy === 'highest') return Number(b.rating) - Number(a.rating);
      if (sortBy === 'lowest') return Number(a.rating) - Number(b.rating);
      return 0;
    });

  // Pagination calculation
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = filteredReviews.slice(indexOfFirstReview, indexOfLastReview);
  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const avgRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + Number(r.rating), 0) / reviews.length).toFixed(1)
    : '4.9';

  return (
    <div style={{ backgroundColor: '#faf8f5', minHeight: '100vh', paddingTop: '100px', paddingBottom: '80px' }}>
      <div className="container" style={{ maxWidth: '1000px' }}>
        
        {/* Back Link */}
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#666', textDecoration: 'none', fontSize: '0.85rem', marginBottom: '24px' }}>
          <ArrowLeft size={16} /> Back to Home
        </Link>

        {/* Hero Header */}
        <div style={{ backgroundColor: '#fff', borderRadius: '20px', padding: '40px', border: '1px solid #EAEDF1', marginBottom: '30px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
          <div className="row align-items-center g-4">
            <div className="col-md-6 text-center text-md-start">
              <span style={{ color: '#D4AF37', letterSpacing: '3px', textTransform: 'uppercase', fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                Guest Testimonials
              </span>
              <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '2.5rem', fontWeight: 700, margin: 0 }}>
                Reviews & Feedback
              </h1>
              <p style={{ color: '#666', marginTop: '8px', marginBottom: 0 }}>
                See what our valued guests have to say about their dining experience.
              </p>
            </div>
            
            <div className="col-md-6 d-flex flex-column flex-sm-row align-items-center justify-content-md-end gap-4 text-center">
              <div style={{ borderRight: '1px solid #f0f0f0', paddingRight: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                  <span style={{ fontSize: '2.5rem', fontWeight: 800, fontFamily: 'Playfair Display, serif', color: '#1A1A1A' }}>{avgRating}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: '1px' }}>
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} size={14} fill={i < Math.round(avgRating) ? '#D4AF37' : 'none'} color="#D4AF37" />
                      ))}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#888', marginTop: '2px' }}>{reviews.length} Verified Reviews</span>
                  </div>
                </div>
              </div>
              
              <button 
                onClick={() => setShowLeaveReview(!showLeaveReview)}
                className="btn btn-gold d-inline-flex align-items-center gap-2"
                style={{ padding: '14px 24px', borderRadius: '50px', fontSize: '0.88rem' }}
              >
                <Plus size={16} /> {showLeaveReview ? 'Hide Form' : 'Write a Review'}
              </button>
            </div>
          </div>
        </div>

        {/* Leave Review collapsible form */}
        {showLeaveReview && (
          <div style={{ marginBottom: '30px' }}>
            <LeaveReview />
          </div>
        )}

        {/* Toolbar: Search, Filters & Sorting */}
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '20px 24px', border: '1px solid #EAEDF1', marginBottom: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
          <div className="row g-3 align-items-center">
            
            {/* Search */}
            <div className="col-md-4">
              <div style={{ position: 'relative' }}>
                <Search size={16} color="#aaa" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)' }} />
                <input 
                  type="text"
                  placeholder="Search reviews..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                  style={{ width: '100%', padding: '10px 14px 10px 38px', borderRadius: '8px', border: '1px solid #EAEDF1', fontSize: '0.88rem' }}
                />
              </div>
            </div>

            {/* Filter by stars */}
            <div className="col-md-4 col-sm-6">
              <div className="d-flex align-items-center gap-2">
                <SlidersHorizontal size={14} color="#777" />
                <select 
                  value={ratingFilter}
                  onChange={(e) => { setRatingFilter(e.target.value); setCurrentPage(1); }}
                  className="form-select form-select-sm"
                  style={{ padding: '10px', borderRadius: '8px', fontSize: '0.88rem', borderColor: '#EAEDF1' }}
                >
                  <option value="all">All Stars</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="2">2 Stars</option>
                  <option value="1">1 Star</option>
                </select>
              </div>
            </div>

            {/* Sorting */}
            <div className="col-md-4 col-sm-6 text-sm-end">
              <div className="d-flex align-items-center justify-content-sm-end gap-2">
                <span style={{ fontSize: '0.85rem', color: '#777' }}>Sort by:</span>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-select form-select-sm"
                  style={{ width: 'auto', padding: '10px', borderRadius: '8px', fontSize: '0.88rem', borderColor: '#EAEDF1' }}
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="highest">Highest Rating</option>
                  <option value="lowest">Lowest Rating</option>
                </select>
              </div>
            </div>

          </div>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#999' }}>
            <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTopColor: '#D4AF37', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 0.8s linear infinite' }}></div>
            <p>Loading reviews...</p>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div style={{ backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #EAEDF1', padding: '60px 40px', textAlign: 'center', color: '#888' }}>
            <MessageSquare size={48} color="#ddd" style={{ marginBottom: '16px' }} />
            <h4 style={{ fontFamily: 'Playfair Display, serif', color: '#333', marginBottom: '8px' }}>No reviews found</h4>
            <p style={{ fontSize: '0.9rem', marginBottom: 0 }}>Try adjusting your filters or search keywords.</p>
          </div>
        ) : (
          <div>
            <div className="row g-4">
              {currentReviews.map((review, idx) => {
                const name = review.display_name || review.name || 'Guest';
                const rating = Number(review.rating) || 5;
                const date = review.created_at 
                  ? new Date(review.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : 'Recently';
                
                return (
                  <div key={review.review_id || idx} className="col-md-6">
                    <div style={{
                      backgroundColor: '#fff', borderRadius: '16px', padding: '28px',
                      border: '1px solid #EAEDF1', height: '100%',
                      display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.015)'
                    }}>
                      <div>
                        {/* Rating & Date */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div style={{ display: 'flex', gap: '2px' }}>
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={13} fill={i < rating ? '#D4AF37' : 'none'} color={i < rating ? '#D4AF37' : '#ddd'} />
                            ))}
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#999' }}>{date}</span>
                        </div>
                        
                        {/* Review text */}
                        <p style={{ fontSize: '0.92rem', color: '#555', lineHeight: 1.8, fontStyle: 'italic', marginBottom: '24px' }}>
                          "{review.comment}"
                        </p>
                      </div>

                      {/* Author Card */}
                      <div className="d-flex align-items-center justify-content-between pt-3" style={{ borderTop: '1px solid #f9f9f9' }}>
                        <div className="d-flex align-items-center gap-3">
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '50%',
                            backgroundColor: avatarColors[idx % avatarColors.length],
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontWeight: 700, fontSize: '0.85rem'
                          }}>
                            {name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#333', display: 'block' }}>{name}</span>
                            <span style={{ fontSize: '0.72rem', color: '#aaa' }}>Verified Diner</span>
                          </div>
                        </div>
                        
                        <div className="d-flex align-items-center gap-1" style={{ color: '#27AE60', fontSize: '0.7rem', fontWeight: 600 }}>
                          <CheckCircle size={12} />
                          <span>Verified</span>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center align-items-center gap-2 mt-5">
                <button 
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    width: '40px', height: '40px', borderRadius: '8px', border: '1px solid #EAEDF1',
                    backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer', color: currentPage === 1 ? '#ccc' : '#D4AF37'
                  }}
                >
                  <ChevronLeft size={18} />
                </button>
                
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button 
                    key={i} 
                    onClick={() => paginate(i + 1)}
                    style={{
                      width: '40px', height: '40px', borderRadius: '8px',
                      backgroundColor: currentPage === i + 1 ? '#D4AF37' : '#fff',
                      color: currentPage === i + 1 ? '#fff' : '#666',
                      fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
                      boxShadow: currentPage === i + 1 ? 'none' : '0 1px 3px rgba(0,0,0,0.04)',
                      border: currentPage === i + 1 ? 'none' : '1px solid #EAEDF1'
                    }}
                  >
                    {i + 1}
                  </button>
                ))}

                <button 
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    width: '40px', height: '40px', borderRadius: '8px', border: '1px solid #EAEDF1',
                    backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', color: currentPage === totalPages ? '#ccc' : '#D4AF37'
                  }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

export default ReviewsPage;
